/**
 * Responsibility: Implement imagen behavior for the ai layer.
 * Scope: Owns ai-layer concerns only; cross-layer orchestration is excluded.
 */
// ============================================================
// IMAGEN.TS — Imagen 4.0 wrapper
// Returns base64 data URL or null on failure/filter.
// ============================================================

import { loadAIConfig } from './config'
import { router } from './router'
import { recordAITelemetry, estimateTokensFromText, getAITelemetry } from './telemetry'
import { emitTaskStart, emitTaskEnd } from './task-events'
import { getProxyAuthorizationHeader } from './proxy-auth'
import { readAdminRuntimeFlags } from '../admin/admin-contract'
import { recordManagedUsageFact, resolveCurrentEntitlement } from '../commerce/commerce-runtime'
const META_ENV = ((import.meta as ImportMeta & { env?: Record<string, string | boolean | undefined> }).env) ?? {}

const PROMPT_CACHE_LIMIT = 120
const promptResultCache = new Map<string, string>()
const inflightRequests = new Map<string, Promise<string | null>>()

export type ImageGenerationStatus = 'ready' | 'missing_config' | 'quota_cooldown'

export function getImageGenerationStatus(): ImageGenerationStatus {
  const windowStart = Date.now() - (24 * 60 * 60 * 1000)
  const telemetry = getAITelemetry()
  
  // 1) Explicit daily quota from admin flags
  const imageQuotaPerDay = readAdminRuntimeFlags().imageQuotaPerDay
  if (imageQuotaPerDay > 0) {
    const recentCount = telemetry.filter(entry => entry.ts >= windowStart && entry.jobType === 'image' && entry.ok).length
    if (recentCount >= imageQuotaPerDay) return 'quota_cooldown'
  }

  // 2) Adaptive cooldown after a 429 (5 minute lockout)
  const COOLDOWN_PERIOD_MS = 5 * 60 * 1000
  const last429 = telemetry.find(e => e.jobType === 'image' && e.error === 'http_429')
  if (last429 && (Date.now() - last429.ts) < COOLDOWN_PERIOD_MS) {
    return 'quota_cooldown'
  }
  
  const providers = router.getProviders()
  const realImageProviders = providers.filter(p => p.id === 'gemini' || p.id === 'stable-diffusion' || p.id === 'vertex-managed')
  return realImageProviders.length > 0 ? 'ready' : 'missing_config'
}

export function resetImageGenerationRuntimeState(): void {
  inflightRequests.clear()
  promptResultCache.clear()
}

function normalizePrompt(promptText: string): string {
  return promptText.trim().replace(/\s+/g, ' ')
}

function trimPromptCache(): void {
  while (promptResultCache.size > PROMPT_CACHE_LIMIT) {
    const firstKey = promptResultCache.keys().next().value
    if (!firstKey) return
    promptResultCache.delete(firstKey)
  }
}

async function requestImage(promptText: string): Promise<string | null> {
  if (getImageGenerationStatus() === 'quota_cooldown') {
    console.warn('[Imagen] Quota cooldown active; skipping image generation attempts until the cooldown expires.')
    return null
  }

  try {
    const dataUrl = await router.route<string>({
      jobType: 'image',
      systemPrompt: 'Generate a relevant image for the following scene.',
      userPrompt: promptText,
    })
    return dataUrl
  } catch (err) {
    console.error('[Imagen] Router failed to generate image:', err)
    return null
  }
}

export async function generateImage(promptText: string): Promise<string | null> {
  const normalizedPrompt = normalizePrompt(promptText)
  if (!normalizedPrompt) return null
  const cached = promptResultCache.get(normalizedPrompt)
  if (cached) return cached
  const inflight = inflightRequests.get(normalizedPrompt)
  if (inflight) return inflight
  const pending = requestImage(normalizedPrompt)
    .then((result) => {
      if (result) {
        promptResultCache.set(normalizedPrompt, result)
        trimPromptCache()
      }
      return result
    })
    .finally(() => {
      inflightRequests.delete(normalizedPrompt)
    })
  inflightRequests.set(normalizedPrompt, pending)
  return pending
}
